import React, { useState, useEffect } from 'react';
import { X, Coffee, BookOpen, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

// Founder reward model isn't finalized yet - keep granting it (so we can tag
// early adopters now) but hide the badge everywhere until launch is decided.
const FOUNDER_BADGE_VISIBLE = false;

const SPINE_COLORS = [
  '#8d6e63','#6d8b74','#7b8cde','#c77dff','#ff9a3c',
  '#4db6ac','#e57373','#a1887f','#4fc3f7','#81c784',
];

const BookSpine = ({ book, index }) => {
  const color = SPINE_COLORS[index % SPINE_COLORS.length];
  const height = 80 + (index % 3) * 20;
  return (
    <div title={book.title} style={{
      width: '22px',
      height: `${height}px`,
      backgroundColor: color,
      borderRadius: '2px 3px 3px 2px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      boxShadow: '2px 0 4px rgba(0,0,0,0.2)',
      cursor: 'default',
      position: 'relative',
      alignSelf: 'flex-end',
    }}>
      <span style={{
        writingMode: 'vertical-rl',
        textOrientation: 'mixed',
        transform: 'rotate(180deg)',
        fontSize: '7px',
        color: 'rgba(255,255,255,0.85)',
        fontWeight: 'bold',
        overflow: 'hidden',
        maxHeight: '90%',
        lineHeight: '1.1',
        padding: '2px',
      }}>
        {book.title}
      </span>
    </div>
  );
};

const UserProfileModal = ({ userId, onClose }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [books, setBooks] = useState([]);
  const [bookCount, setBookCount] = useState(0);
  const [teaCount, setTeaCount] = useState(0);
  const [myTeaToday, setMyTeaToday] = useState(false);
  const [loading, setLoading] = useState(true);
  const [teaSending, setTeaSending] = useState(false);

  useEffect(() => {
    if (userId) loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    setLoading(true);
    const [profileRes, teaRes] = await Promise.all([
      supabase.from('profiles').select('id, username, avatar_url, nook_public, role, is_founder').eq('id', userId).single(),
      supabase.from('kudos_tea').select('id, from_user_id', { count: 'exact' }).eq('to_user_id', userId),
    ]);

    if (profileRes.data) {
      setProfile(profileRes.data);
      if (profileRes.data.nook_public) {
        const { data: booksData, count: booksCount } = await supabase
          .from('books')
          .select('id, title, status', { count: 'exact' })
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(40);
        setBooks(booksData || []);
        setBookCount(booksCount ?? (booksData || []).length);
      }
    }

    if (teaRes.data) {
      setTeaCount(teaRes.count || 0);
      if (user) {
        const today = new Date().toISOString().split('T')[0];
        const { data: myTea } = await supabase
          .from('kudos_tea')
          .select('id')
          .eq('from_user_id', user.id)
          .eq('to_user_id', userId)
          .eq('sent_date', today);
        setMyTeaToday(myTea && myTea.length > 0);
      }
    }
    setLoading(false);
  };

  const handleLeaveTea = async () => {
    if (!user || myTeaToday || teaSending) return;
    setTeaSending(true);
    const today = new Date().toISOString().split('T')[0];
    const { error } = await supabase.from('kudos_tea').insert({ from_user_id: user.id, to_user_id: userId, sent_date: today });
    if (!error) {
      setMyTeaToday(true);
      setTeaCount(c => c + 1);
      // Notify the recipient
      await supabase.from('notifications').insert({
        user_id: userId,
        title: 'Someone left you tea ☕',
        content: `${user.user_metadata?.username || 'A reader'} left a cup of tea on your porch.`,
        icon: 'coffee',
        category: 'personal',
        is_read: false,
      });
    }
    setTeaSending(false);
  };

  const readBooks = books.filter(b => b.status === 'read');
  const readingBooks = books.filter(b => b.status === 'reading');
  const tbrBooks = books.filter(b => b.status === 'tbr');

  if (!userId) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={closeBtn}><X size={20} /></button>

        {loading ? (
          <p style={{ color: 'var(--text-soft)', textAlign: 'center', padding: '2rem' }}>Loading sanctuary...</p>
        ) : !profile ? (
          <p style={{ color: 'var(--text-soft)', textAlign: 'center', padding: '2rem' }}>Reader not found.</p>
        ) : (
          <>
            {/* Header */}
            <div style={headerStyle}>
              <div style={avatarStyle}>
                {profile.avatar_url ? (
                  <span style={{ fontSize: '2rem' }}>{profile.avatar_url}</span>
                ) : (
                  <BookOpen size={28} color="var(--text-soft)" />
                )}
              </div>
              <div>
                <h3 style={{ margin: 0, color: 'var(--text-main)' }}>{profile.username || 'Anonymous'}</h3>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                  {FOUNDER_BADGE_VISIBLE && profile.is_founder && <span style={{ fontSize: '0.65rem', backgroundColor: '#ffd700', color: '#5a3e00', fontWeight: 'bold', padding: '1px 6px', borderRadius: '8px' }}>🌿 Founder</span>}
                  {profile.role === 'employee' && <span style={{ fontSize: '0.65rem', backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', padding: '1px 6px', borderRadius: '8px' }}>👔 Employee</span>}
                  {profile.role === 'partner' && <span style={{ fontSize: '0.65rem', backgroundColor: '#f3e5f5', color: '#6a1b9a', fontWeight: 'bold', padding: '1px 6px', borderRadius: '8px' }}>🤝 Partner</span>}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {profile.nook_public ? <Eye size={12} /> : <EyeOff size={12} />}
                  {profile.nook_public ? 'Nook is open to visitors' : 'Private sanctuary'}
                </div>
              </div>
            </div>

            {/* Tea Section */}
            <div style={teaRowStyle}>
              <div style={{ color: 'var(--text-soft)', fontSize: '0.85rem' }}>
                ☕ <strong style={{ color: 'var(--text-main)' }}>{teaCount}</strong> cups left on the porch
              </div>
              {user && user.id !== userId && (
                <button
                  onClick={handleLeaveTea}
                  disabled={myTeaToday || teaSending}
                  style={{
                    ...teaBtnStyle,
                    opacity: myTeaToday ? 0.5 : 1,
                    cursor: myTeaToday ? 'default' : 'pointer',
                  }}
                >
                  <Coffee size={14} />
                  {myTeaToday ? 'Tea left ✓' : teaSending ? 'Leaving...' : 'Leave Tea'}
                </button>
              )}
            </div>

            {/* Nook */}
            {profile.nook_public ? (
              <div style={nookSection}>
                <div style={nookHeader}>
                  <Eye size={14} color="var(--accent)" />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-soft)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Their Nook
                  </span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-soft)', marginLeft: 'auto' }}>{bookCount} books</span>
                </div>

                {books.length === 0 ? (
                  <p style={{ color: 'var(--text-soft)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>Their shelves are empty.</p>
                ) : (
                  <>
                    {readingBooks.length > 0 && (
                      <div style={shelfSection}>
                        <div style={shelfLabel}>Currently Reading</div>
                        <div style={spineRow}>
                          {readingBooks.map((b, i) => <BookSpine key={b.id} book={b} index={i} />)}
                        </div>
                        <div style={shelfPlank} />
                      </div>
                    )}
                    {readBooks.length > 0 && (
                      <div style={shelfSection}>
                        <div style={shelfLabel}>Read ({readBooks.length})</div>
                        <div style={spineRow}>
                          {readBooks.slice(0, 30).map((b, i) => <BookSpine key={b.id} book={b} index={i + 3} />)}
                        </div>
                        <div style={shelfPlank} />
                      </div>
                    )}
                    {tbrBooks.length > 0 && (
                      <div style={shelfSection}>
                        <div style={shelfLabel}>Want to Read ({tbrBooks.length})</div>
                        <div style={spineRow}>
                          {tbrBooks.slice(0, 20).map((b, i) => <BookSpine key={b.id} book={b} index={i + 7} />)}
                        </div>
                        <div style={shelfPlank} />
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-soft)', fontSize: '0.85rem' }}>
                <EyeOff size={24} style={{ marginBottom: '8px', opacity: 0.4 }} />
                <p style={{ margin: 0 }}>This reader keeps their sanctuary private.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1200, padding: '1rem',
};

const modalStyle = {
  backgroundColor: 'var(--bg-card)',
  borderRadius: '20px',
  padding: '1.5rem',
  width: '100%',
  maxWidth: '460px',
  maxHeight: '85vh',
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
  border: '1px solid var(--border)',
};

const closeBtn = {
  position: 'absolute', top: '14px', right: '14px',
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-soft)', padding: '4px',
};

const headerStyle = {
  display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem',
};

const avatarStyle = {
  width: '56px', height: '56px', borderRadius: '50%',
  backgroundColor: 'var(--bg-app)', border: '1px solid var(--border)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};

const teaRowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '10px 12px', backgroundColor: 'var(--bg-app)',
  borderRadius: '12px', marginBottom: '1rem',
  border: '1px solid var(--border)',
};

const teaBtnStyle = {
  display: 'flex', alignItems: 'center', gap: '6px',
  padding: '8px 14px', borderRadius: '20px',
  border: 'none', backgroundColor: 'var(--accent)',
  color: 'white', fontSize: '0.8rem', fontWeight: 'bold',
};

const nookSection = {
  backgroundColor: 'var(--bg-app)',
  borderRadius: '12px',
  padding: '12px',
  border: '1px solid var(--border)',
};

const nookHeader = {
  display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px',
};

const shelfSection = { marginBottom: '12px' };

const shelfLabel = {
  fontSize: '0.7rem', color: 'var(--text-soft)', fontWeight: 'bold',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px',
};

const spineRow = {
  display: 'flex', alignItems: 'flex-end', gap: '2px',
  minHeight: '100px', overflowX: 'auto', paddingBottom: '4px',
};

const shelfPlank = {
  height: '6px', backgroundColor: '#8d6e63',
  borderRadius: '3px', marginTop: '2px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
};

export default UserProfileModal;
